/**
 * vaultUnlock.ts — resolving which key opens a project's vault, and moving a
 * project from v1 to v2.
 *
 * This lives outside VaultScreen deliberately. The screen is ~3,500 lines of
 * UI; the decisions here are the ones that can permanently destroy a user's
 * credentials, and they need to be readable and testable without mounting a
 * React tree.
 *
 * ── The states ─────────────────────────────────────────────────────────────
 * Resolution is server-first: ask which scheme the project is on, THEN load a
 * cached key for that scheme. The reverse order — the tempting fast path — is
 * how a device that cached the v1 key before another device migrated ends up
 * decrypting v2 ciphertext with it and showing the user a vault full of
 * garbage. One small GET removes that whole class of bug, on a path that was
 * already about to fetch the entry list anyway.
 *
 * ── Two kinds of v2 ────────────────────────────────────────────────────────
 * Vaults move to a random per-plan key that the server holds wrapped under a key
 * outside the database (backend utilities/vault-server-key.js). Nobody types or
 * keeps anything, on any device, and a new device simply opens. The server can
 * therefore open these vaults. Nothing in the database can.
 *
 * A few older vaults are wrapped under the owner's account root key instead.
 * Those still open with that key or the owner's recovery code.
 *
 * `foreign_key` deserves its own state rather than an error. It means the
 * project's vault IS v2 but holds no wrap for this principal, which in
 * practice means a plan under an account root key was transferred: the wrap
 * belongs to the previous owner. Saying so is much better than the v1
 * behaviour, where transfer silently produced unreadable items (account_id is
 * a v1 KDF input, and transfer flips it).
 */

import { getAccountRootKey } from './accountRootKey';
import {
  deriveVaultKey,
  cacheVaultKey,
  loadVaultKey,
  clearVaultKey,
  encryptCredential,
  decryptCredential,
  bytesToHex,
  hexToBytes,
  type DecryptedCredential,
} from './vaultCrypto';
import {
  VAULT_KEY_SCHEME_V1,
  VAULT_KEY_SCHEME_V2,
  generateVaultKey,
  openVaultKey,
  vekFingerprint,
  type VaultKeyScheme,
} from './vaultKeyV2';
import {
  fetchVaultSalt,
  fetchVaultKeyWrap,
  fetchVaultServerKey,
  migrateVaultScheme,
} from '../store/actions/vault';

export interface VaultIdentity {
  accountId: string;
  projectId: number | string;
  cognitoId: string;
}

export type VaultUnlock =
  | { status: 'ready'; vek: Uint8Array; scheme: VaultKeyScheme }
  /** v2 wrap exists for us, but this device has no account root key. */
  | { status: 'needs_recovery' }
  /** v2 vault wrapped for someone else — a transferred plan. */
  | { status: 'foreign_key' }
  | { status: 'denied' }
  | { status: 'error'; message?: string };

/** Resolve the key that opens this project's vault. */
export async function openProjectVault(id: VaultIdentity): Promise<VaultUnlock> {
  const { accountId, projectId, cognitoId } = id;

  const keyResult = await fetchVaultKeyWrap(projectId, accountId, cognitoId);
  if (keyResult.failed) {
    if (keyResult.status === 403) return { status: 'denied' };
    return { status: 'error' };
  }

  // ── v2, held by the server ───────────────────────────────────────────────
  if (keyResult.serverKey) {
    const { vek_fingerprint: expected } = keyResult.serverKey;
    // The fingerprint keeps a key cached for an earlier wrap of this plan from
    // sealing new items no other device could open.
    const cached = await loadVaultKey(projectId, VAULT_KEY_SCHEME_V2);
    if (cached && vekFingerprint(cached) === expected) {
      return { status: 'ready', vek: cached, scheme: VAULT_KEY_SCHEME_V2 };
    }

    const vekHex = await fetchVaultServerKey(projectId, accountId, cognitoId);
    const vek = vekHex ? hexToBytes(vekHex) : null;
    // A failure, never a reason to fall back: this vault's items aren't on v1.
    if (!vek || vekFingerprint(vek) !== expected) return { status: 'error' };

    await cacheVaultKey(projectId, vek, VAULT_KEY_SCHEME_V2).catch(() => {});
    return { status: 'ready', vek, scheme: VAULT_KEY_SCHEME_V2 };
  }

  // ── v2, under the owner's account root key ───────────────────────────────
  if (keyResult.wrap) {
    const cached = await loadVaultKey(projectId, VAULT_KEY_SCHEME_V2);
    if (cached) return { status: 'ready', vek: cached, scheme: VAULT_KEY_SCHEME_V2 };

    const rootKey = await getAccountRootKey(accountId);
    // No root key on this device: iCloud Keychain hasn't delivered it, or this
    // is Android, or the user signed out. The recovery mnemonic flow is the
    // way back — not an error, and not a reason to fall back to v1.
    if (!rootKey) return { status: 'needs_recovery' };

    const vek = openVaultKey(keyResult.wrap as any, rootKey, accountId, projectId);
    if (!vek) {
      // The wrap is present and the device has A root key, but it doesn't open
      // this wrap. Deliberately not distinguishing "wrong key" from "tampered
      // ciphertext": the user's next step is identical either way.
      return { status: 'error', message: 'wrap_unopenable' };
    }

    await cacheVaultKey(projectId, vek, VAULT_KEY_SCHEME_V2).catch(() => {});
    return { status: 'ready', vek, scheme: VAULT_KEY_SCHEME_V2 };
  }

  // ── v2 vault, but not ours ───────────────────────────────────────────────
  if (keyResult.projectHasV2) return { status: 'foreign_key' };

  // ── v1 ───────────────────────────────────────────────────────────────────
  const cachedV1 = await loadVaultKey(projectId, VAULT_KEY_SCHEME_V1);
  if (cachedV1) return { status: 'ready', vek: cachedV1, scheme: VAULT_KEY_SCHEME_V1 };

  const saltResult = await fetchVaultSalt(projectId, accountId, cognitoId);
  if (!saltResult.salt) {
    if (saltResult.status === 403) return { status: 'denied' };
    return { status: 'error', message: saltResult.message };
  }

  const derived = await deriveVaultKey(accountId, projectId, saltResult.salt);
  await cacheVaultKey(projectId, derived, VAULT_KEY_SCHEME_V1).catch(() => {});
  return { status: 'ready', vek: derived, scheme: VAULT_KEY_SCHEME_V1 };
}

/**
 * The only fields migration reads. Deliberately narrower than
 * EncryptedCredential: this path must not depend on label, hints, change log
 * or anything else presentational, and saying so in the type keeps it that
 * way.
 */
export interface MigratableEntry {
  id: number;
  encrypted_data: string;
  iv: string;
  auth_tag: string;
  key_scheme?: number;
}

export type MigrationOutcome =
  | { status: 'migrated'; vek: Uint8Array; count: number }
  | { status: 'already' }
  /** At least one item wouldn't decrypt under v1; migrating would destroy it. */
  | { status: 'undecryptable'; count: number }
  | { status: 'retry'; message?: string }
  | { status: 'error'; message?: string };

/**
 * Move a project's vault from the v1 derived key to a random VEK the server
 * holds wrapped. Runs by itself when a vault opens on v1, so nothing here asks
 * the person for anything, and until the server confirms, the v1 vault is
 * exactly as it was.
 *
 * Three properties this function exists to guarantee:
 *
 *  1. The item set comes from the SERVER, fetched fresh here. Migrating the
 *     Redux list would re-encrypt whatever that store happened to hold — which
 *     a second device's write, or a snapshot restore, can make stale. The
 *     server then re-checks the id set and each item's ciphertext inside its
 *     transaction, so a race between this fetch and the commit is caught rather
 *     than committed.
 *
 *  2. Nothing is sent unless EVERY item decrypted. A single failure means the
 *     v1 key doesn't open that row, and writing a v2 blob over it would turn a
 *     recoverable problem into a permanent one.
 *
 *  3. The result is read back before it is sent. Once this commits, the only
 *     copy of each item is the ciphertext built below. A bug in that has to
 *     fail here, while the v1 rows still exist. The server proves its side the
 *     same way, opening its own wrap before it writes anything.
 */
export async function migrateProjectVaultToV2(
  id: VaultIdentity,
  v1Vek: Uint8Array,
  deps: { fetchEntries: () => Promise<MigratableEntry[]> },
): Promise<MigrationOutcome> {
  const { accountId, projectId, cognitoId } = id;

  let list: MigratableEntry[];
  try {
    list = await deps.fetchEntries();
  } catch (err: any) {
    return { status: 'error', message: err?.message };
  }

  const legacy = list.filter((e) => Number(e.key_scheme ?? 1) !== VAULT_KEY_SCHEME_V2);

  // Decrypt everything BEFORE minting anything. An abort here leaves the vault
  // exactly as it was.
  const plain: { entry: MigratableEntry; data: DecryptedCredential }[] = [];
  let undecryptable = 0;
  for (const entry of legacy) {
    const data = decryptCredential(v1Vek, entry.encrypted_data, entry.iv, entry.auth_tag);
    if (!data) { undecryptable += 1; continue; }
    plain.push({ entry, data });
  }
  // Counted rather than short-circuited so the message can say how many, and
  // so a user with one broken row from some older incident isn't told
  // something vague about "an item".
  if (undecryptable > 0) return { status: 'undecryptable', count: undecryptable };

  const vek = await generateVaultKey();

  const items = [];
  for (const { entry, data } of plain) {
    const re = await encryptCredential(vek, data);
    const back = decryptCredential(vek, re.encrypted_data, re.iv, re.auth_tag);
    if (JSON.stringify(back) !== JSON.stringify(data)) {
      return { status: 'error', message: 'item_unopenable' };
    }
    items.push({
      id: entry.id,
      encrypted_data: re.encrypted_data,
      iv: re.iv,
      auth_tag: re.auth_tag,
      // What this was decrypted from. The server refuses the move if the row has
      // changed since, or a newer edit would be overwritten with this older text.
      source_auth_tag: entry.auth_tag,
    });
  }

  const res = await migrateVaultScheme(projectId, accountId, cognitoId, {
    vek_hex: bytesToHex(vek),
    vek_fingerprint: vekFingerprint(vek),
    items,
  });

  if (!res.success) {
    // The server rolls back on any failure, so the vault is untouched and a
    // retry is safe. `vault_set_changed` / `vault_mixed_scheme` specifically
    // mean "re-read and try again", which the caller surfaces as such.
    const retryable = res.errorCode === 'vault_set_changed'
      || res.errorCode === 'vault_mixed_scheme'
      || res.errorCode === 'vault_key_conflict';
    return retryable
      ? { status: 'retry', message: res.message }
      : { status: 'error', message: res.message };
  }

  if (res.already) return { status: 'already' };

  // Only now is the v2 key real. Drop BOTH cached slots first — that clears
  // the stale v1 key, which is now a secret on disk that opens nothing — then
  // write the v2 slot. Order matters: clearVaultKey clears both.
  await clearVaultKey(projectId).catch(() => {});
  await cacheVaultKey(projectId, vek, VAULT_KEY_SCHEME_V2).catch(() => {});
  return { status: 'migrated', vek, count: items.length };
}
